import { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import { NotAllowedError } from '@backstage/errors';
import { AsyncTasksApi } from '@internal/backstage-plugin-devops-async-tasks-react';
import {
  AsyncTaskType,
  AsyncTaskInfo,
  AsyncTaskSchedulerInfo,
  AsyncTaskStructure,
} from '@internal/backstage-plugin-devops-async-tasks-common';
import { DefaultApiClient } from '@internal/backstage-plugin-devops-react';
import { ScanStatusResponse } from '@internal/backstage-plugin-catalog-devops-common';

export class AsyncTasksApiClient
  extends DefaultApiClient
  implements AsyncTasksApi
{
  public constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
  }) {
    super({
      backendName: 'devops-async-tasks',
      ...options,
    });
  }
  async startAsyncTask(
    taskType: AsyncTaskType,
    params: Record<string, any>,
    entityId?: string,
  ): Promise<number> {
    const urlSegment = '/v1/task/start';
    const response = await this.post<{
      taskType: AsyncTaskType;
      params: Record<string, any>;
      entityId?: string;
    }>(urlSegment, { taskType, params, entityId });
    if (!response.succeeded) {
      if (response.status === 403) {
        throw new NotAllowedError('You are not authorized to start this task');
      }
      throw new Error(response.error);
    }
    return response.body.taskId;
  }

  async getActiveTasks(
    entityId: string,
    taskType: AsyncTaskType,
  ): Promise<{
    tasks: AsyncTaskInfo[];
    scanStatus: ScanStatusResponse | undefined;
  }> {
    const urlSegment = `/v1/task/${entityId}/${taskType}`;
    const response = await this.get(urlSegment);
    if (!response.succeeded) {
      throw new Error(response.error);
    }
    return {
      tasks: response.body.tasks as AsyncTaskInfo[],
      scanStatus: response.body.scanStatus as ScanStatusResponse | undefined,
    };
  }

  async getTaskInfo(taskId: number): Promise<{
    task: AsyncTaskInfo;
    scanStatus: ScanStatusResponse | undefined;
  }> {
    const urlSegment = `/v1/task/${taskId}`;
    const response = await this.get(urlSegment);
    if (!response.succeeded) {
      throw new Error(response.error);
    }
    return {
      task: response.body.task as AsyncTaskInfo,
      scanStatus: response.body.scanStatus as ScanStatusResponse | undefined,
    };
  }

  async getTaskStructure(taskType: AsyncTaskType): Promise<AsyncTaskStructure> {
    const urlSegment = `/v1/task-structure/${encodeURIComponent(taskType)}`;
    const response = await this.get(urlSegment);
    if (!response.succeeded) {
      throw new Error(response.error);
    }
    return response.body.structure as AsyncTaskStructure;
  }

  async getTaskStructures(): Promise<AsyncTaskStructure[]> {
    const urlSegment = '/v1/task-structures';
    const response = await this.get(urlSegment);
    if (!response.succeeded) {
      throw new Error(response.error);
    }
    return response.body.structures as AsyncTaskStructure[];
  }

  async getSchedulersInfo(): Promise<AsyncTaskSchedulerInfo[]> {
    const urlSegment = '/v1/schedulers';
    const response = await this.get(urlSegment);
    if (!response.succeeded) {
      throw new Error(response.error);
    }
    return response.body.schedulers as AsyncTaskSchedulerInfo[];
  }
}
